// The seam the web diagnostics UI drives, and the real thing behind it.
//
// The brain owns diagnostics end to end (docs/10 §1): the ring, redaction,
// batching, persistence, upload scheduling and backoff. The native hosts reach
// it through the five `diag.*` request methods (brain `runtime/vocabulary.ts`);
// the web app composes the brain in-process, so it reaches the same
// `DiagCenter` directly — but through the SAME five operations, so the three
// hosts cannot drift and none of them can reformat what the user is shown.
//
// One rule belongs to the host and is bound in here: docs/10 §4's "the toggle
// can only ever subtract". While auto-send is explicitly off, nothing is
// OFFERED for upload and nothing is sent — not by the schedule, not by a
// severe-failure prompt, not by a button. The brain enforces it too; this is
// the half that keeps the app from offering what it has promised not to do.

import {
  DiagCenter,
  intakeSender,
  restSender,
  type DiagSender,
  type UploadPayload,
} from '@merkury/brain/runtime/diag/index.js';
import { logRing, pushLog } from '@merkury/brain/runtime/logRing.js';
import { defaultFetch } from '@merkury/brain/lib/http.js';
import { ENDPOINTS_STATE_KEY } from '@merkury/brain/storage/kvKeys.js';
import type { KvStore } from '@merkury/brain/storage/kv.js';
import type { EndpointList } from '@merkury/protocol';
import { resolveDeviceKv } from '../../../storage/kvStores.js';
import { shellRestOrigin } from '../../../shell/restOrigin.js';
import { webBuildInfo, webSnapshotJson, type WebSnapshotInput } from './context.js';
import { appLog } from './appLog.js';
import type { BundleBuildInfo, BundleLogLine } from './bundle.js';

/** Consent + queue state, as the screen renders it. */
export interface DiagnosticsState {
  devMode: boolean;
  /** Effective auto-send: ON by default under dev mode, the answer always wins. */
  autoSend: boolean;
  /** The user's explicit answer, or undefined if never asked. */
  autoSendChoice: boolean | undefined;
  /** May anything be OFFERED for upload at all (docs/10 §4)? */
  offersUpload: boolean;
  /** The dev-unlock disclosure is owed and has not been shown. */
  disclosurePending: boolean;
  queued: number;
  lastOutcome: string | null;
}

/** `DiagCenter.preview()`'s reply, as the screen needs it. */
export interface DiagnosticsPreview {
  reportId: string;
  /** The exact bytes that would be uploaded. Never re-rendered by the host. */
  bytes: string;
  /** Size of the gzip a sender would put on the wire — a transport fact, not a
   *  second document for a host to show instead of the one the user can read. */
  gzipSize: number | null;
}

export interface DiagnosticsGateway {
  /**
   * Entering the screen. Mirrors the dev-unlock flag into the brain's consent
   * record — a re-assertion arms nothing, only a genuinely fresh unlock still
   * owing a disclosure does — and answers the state to render.
   */
  open(devMode: boolean): Promise<DiagnosticsState>;
  setAutoSend(on: boolean): Promise<DiagnosticsState>;
  markDisclosed(): Promise<DiagnosticsState>;
  /** Leaving dev mode, so a later unlock discloses again. */
  setDevMode(on: boolean): Promise<DiagnosticsState>;
  /** The §4 promise: the exact bytes, cutting a batch first if none is queued. */
  preview(): Promise<DiagnosticsPreview | null>;
  /** True when the report was handed to the delivery ladder. */
  sendNow(): Promise<boolean>;
  /** The brain's ring — structured `diag.*` events and its own notes. */
  brainLog(): readonly BundleLogLine[];
  /** This host's ring — everything the app said through the console. */
  appLog(): readonly BundleLogLine[];
  snapshotJson(state: DiagnosticsState | null): string;
  buildInfo(): BundleBuildInfo;
}

/** What the gateway drives: the centre, plus the KV the snapshot page reads. */
export interface DiagWiring {
  centre: DiagCenter;
  kv: KvStore;
}

function stateOf(centre: DiagCenter): DiagnosticsState {
  const slice = centre.slice();
  const choice = centre.consent().autoSendChoice();
  return {
    devMode: slice.devMode,
    autoSend: slice.autoSend,
    autoSendChoice: choice,
    // Off means off: with the toggle explicitly off nothing goes even when the
    // user is asked (docs/10 §4, mirroring `Consent.allowsConsented`).
    offersUpload: choice !== false,
    disclosurePending: slice.disclosure,
    queued: slice.queued,
    lastOutcome: slice.lastOutcome,
  };
}

/** The snapshot page's endpoint block: the adopted signed list as the call
 *  engine persisted it, reduced to counts and one host. */
async function endpointFacts(kv: KvStore): Promise<WebSnapshotInput['endpoints']> {
  try {
    const state = await kv.get<{ list?: EndpointList; lastGoodUrl?: string }>(ENDPOINTS_STATE_KEY);
    const list = state?.list;
    if (!list) return undefined;
    return {
      version: list.version,
      signaling: list.endpoints.length,
      turn: list.turn?.length ?? 0,
      doh: list.doh?.length ?? 0,
      diag: list.diag?.length ?? 0,
      ...(state?.lastGoodUrl === undefined ? {} : { lastGoodUrl: state.lastGoodUrl }),
    };
  } catch {
    return undefined;
  }
}

/** A diagnostics intake as the signed list carries it (docs/10 §5 rung 3). */
type Intake = { url: string };

/**
 * How long a read of the signed list's `diag` array stays fresh.
 * `DiagCenterOptions.senders` is documented as re-read per flush precisely so a
 * rotated endpoint list changes the ladder without restarting anything — and
 * rung 3 is the rung that exists for when `merkury.app` itself is dead, so a
 * list frozen at boot is exactly the failure this ladder is about.
 */
export const INTAKE_TTL_MS = 60_000;

export interface IntakeCache {
  /** The intakes as last read. `senders()` is synchronous, so a stale read is
   *  refreshed in the background and freshness is at worst one flush late. */
  current(): readonly Intake[];
  /** Read now. Awaited once at wiring time, so the first flush is not blind. */
  refresh(): Promise<void>;
}

export function intakeCache(
  kv: KvStore,
  options: { ttlMs?: number; now?: () => number } = {},
): IntakeCache {
  const ttlMs = options.ttlMs ?? INTAKE_TTL_MS;
  const now = options.now ?? Date.now;
  let intakes: readonly Intake[] = [];
  let readAtMs = Number.NEGATIVE_INFINITY;
  let inFlight: Promise<void> | null = null;

  async function read(): Promise<void> {
    try {
      const state = await kv.get<{ list?: EndpointList }>(ENDPOINTS_STATE_KEY);
      intakes = state?.list?.diag ?? [];
    } catch {
      // Keep the last list. Storage that will not answer is no reason to drop
      // the only rung that survives the API's own domain being blocked.
    }
    readAtMs = now();
  }

  function refresh(): Promise<void> {
    if (inFlight !== null) return inFlight;
    const started = read().finally(() => {
      if (inFlight === started) inFlight = null;
    });
    inFlight = started;
    return started;
  }

  return {
    current() {
      if (now() - readAtMs >= ttlMs) void refresh();
      return intakes;
    },
    refresh,
  };
}

/** The brain's own closed vocabulary for `net.kind` (diag/events.ts). */
const BRAIN_NETWORK_KINDS: ReadonlySet<string> = new Set([
  'wifi',
  'cellular',
  'ethernet',
  'other',
  'none',
  'unknown',
]);

/** `navigator.connection`, which no lib.dom version declares the same way. */
function networkKind(): string | undefined {
  const connection = (
    globalThis as {
      navigator?: { connection?: { type?: string; effectiveType?: string } };
    }
  ).navigator?.connection;
  return connection?.type ?? connection?.effectiveType;
}

/**
 * The shipping gateway: the brain's diagnostics centre, driven directly.
 * `load` is a thunk so the centre (and its KV round-trip) is built once, on
 * first use, and so tests can hand in a centre of their own.
 */
export function diagCenterGateway(load: () => Promise<DiagWiring>): DiagnosticsGateway {
  let endpoints: WebSnapshotInput['endpoints'];

  const withCentre = async (
    fn: (centre: DiagCenter) => Promise<void> | void,
  ): Promise<DiagnosticsState> => {
    const { centre } = await load();
    await fn(centre);
    return stateOf(centre);
  };

  return {
    async open(devMode) {
      const { centre, kv } = await load();
      await centre.setDevMode(devMode);
      endpoints = await endpointFacts(kv);
      return stateOf(centre);
    },
    setAutoSend: (on) => withCentre((centre) => centre.setAutoSend(on)),
    markDisclosed: () => withCentre((centre) => centre.markDisclosed()),
    setDevMode: (on) => withCentre((centre) => centre.setDevMode(on)),
    async preview() {
      const { centre } = await load();
      // "Show me what you would send" answers with this session rather than
      // with silence: cut a batch when nothing is queued yet.
      let payload: UploadPayload | null = await centre.preview();
      if (payload === null && (await centre.capture('manual')) !== null) {
        payload = await centre.preview();
      }
      if (payload === null) return null;
      return {
        reportId: payload.reportId,
        bytes: payload.bytes,
        gzipSize: payload.gzip === null ? null : payload.gzip.length,
      };
    },
    async sendNow() {
      const { centre } = await load();
      // Refused outright while the toggle is off — the button is not offered
      // then either, and both halves have to agree.
      if (!stateOf(centre).offersUpload) return false;
      await centre.sendNow();
      return true;
    },
    brainLog: () => logRing().map((line) => ({ ts: line.ts, level: line.level, msg: line.msg })),
    appLog,
    snapshotJson(state) {
      const kind = networkKind();
      const online = (globalThis as { navigator?: { onLine?: boolean } }).navigator?.onLine;
      return webSnapshotJson({
        atMs: Date.now(),
        build: webBuildInfo(),
        diag: {
          prompt: null,
          disclosure: state?.disclosurePending ?? false,
          autoSend: state?.autoSend ?? false,
          devMode: state?.devMode ?? false,
          queued: state?.queued ?? 0,
          lastOutcome: state?.lastOutcome ?? null,
        },
        restBase: shellRestOrigin().base(),
        endpoints,
        online,
        ...(kind === undefined ? {} : { networkKind: kind }),
      });
    },
    buildInfo: webBuildInfo,
  };
}

let shared: Promise<DiagWiring> | null = null;
let built: DiagCenter | null = null;
let callActive = false;

/**
 * The shell tells diagnostics when a call is up. Uploads are NEVER sent during
 * a call (docs/10 §5) — an upload competing with media is the prime directive
 * inverted — and the same fact throttles the ring's writes.
 *
 * Applied SYNCHRONOUSLY: a call starting is the moment an in-flight flush
 * checks the flag, and a hop through the wiring promise would let one through.
 * Held here as well as in the centre because a call can start before anything
 * has opened the centre, and the fact must survive that.
 */
export function noteWebCall(inCall: boolean): void {
  callActive = inCall;
  built?.noteCall(inCall);
}

/** What the composition root may hand the centre. */
export interface WebDiagDeps {
  /** The shell's KV instance — one handle, one owner. First call wins. */
  kv?: KvStore;
  /** Rungs ABOVE the REST ladder: docs/10 §5 rung 1, the socket that is already
   *  open, which belongs to the signaling client. Re-read per flush like the
   *  rest of the list, so a socket that comes up later is used. */
  aheadOf?: () => readonly DiagSender[];
}

/**
 * The web host's diagnostics centre. One per process, like the brain's log ring
 * it reads from and the KV slots it owns; built lazily so a browser that never
 * opens Developer never pays for it.
 *
 * The delivery ladder here is rungs 2 and 3 (docs/10 §5): the REST origin
 * router, which carries the endpoint failover, and the independent intakes from
 * the signed list — plus whatever the composition root puts above them.
 */
export function webDiagWiring(deps: WebDiagDeps = {}): Promise<DiagWiring> {
  shared ??= (async (): Promise<DiagWiring> => {
    const kv = deps.kv ?? (await resolveDeviceKv());
    // The UNDECORATED transport. Routing an upload through a fetch that records
    // `http.attempt` would make every failed upload the content of the next
    // report, which fails the same way — a telemetry feedback loop on exactly
    // the blocked network the reports exist to describe.
    const fetchFn = defaultFetch();
    const build = webBuildInfo();
    const intakes = intakeCache(kv);
    // Awaited: `start()` schedules a flush, and a first flush that has not read
    // the list yet has no rung 3 at all.
    await intakes.refresh();
    const centre = new DiagCenter({
      kv,
      senders: () => [
        ...(deps.aheadOf?.() ?? []),
        restSender({ fetchFn, baseUrl: () => shellRestOrigin().base() }),
        intakeSender({ fetchFn, intakes: () => intakes.current() }),
      ],
      build: {
        version: build.appVersion,
        ...(build.appBuild === undefined ? {} : { commit: build.appBuild }),
        entry: 'web',
      },
      // The centre's own notes belong in the brain's ring, which is the page
      // that shows them — never in the console, which this host mirrors into
      // the app ring and would loop.
      log: (level, msg) => pushLog(level, msg),
    });
    // The host's third contribution (docs/10 §1): coarse device context. A
    // browser has no telephony, so country and carrier are an honest gap here —
    // `navigator.connection` is the one network fact it will state.
    const kind = networkKind();
    centre.setDeviceContext({
      platform: 'web',
      appVersion: build.appVersion,
      ...(kind !== undefined && BRAIN_NETWORK_KINDS.has(kind) ? { networkKind: kind } : {}),
    });
    // A call that started before anything opened the centre still gates it.
    centre.noteCall(callActive);
    built = centre;
    await centre.start();
    return { centre, kv };
  })();
  return shared;
}

/** Drop the process-wide centre and the call flag. Tests only. */
export function resetWebDiagnosticsForTest(): void {
  shared = null;
  built = null;
  callActive = false;
}

/** The gateway the Developer screen uses when it is not given one. */
export const webDiagnostics: DiagnosticsGateway = diagCenterGateway(webDiagWiring);
